import {
  checkInputFields,
  showLoading,
  warningMessage,
} from "./helperFunctions";
import { zuruecksetzenPopupAnzeige } from "./popUpFunctions";
import { Icon, Style } from "ol/style.js";
import { Feature } from "ol";
import { fromLonLat } from "ol/proj.js";
import { Point } from "ol/geom";
import VectorLayer from "ol/layer/Vector.js";
import VectorSource from "ol/source/Vector.js";
import { hideLayer, removeMapLayer } from "./mapFunctions";
import { createCenterMapLink, getTreeVitalityIcon } from "./helperFunctions";
import { getActualMap, getGlobalMap } from "./globalContext";
import * as geoServerServices from "./Services/GeoServerServices";
import { setStatistikDaten } from "./statisticFunctions.js";
import {
  calculateCo2StorageByDimensions,
  cmToMeter,
  getDryDensity,
  circumferenceToDiameter,
} from "./co2calculator";

import { GEOSERVER_BASE_URL, GEOSERVER_WORKSPACE } from "./config/api.js";
import { renderSearchResultTable, renderCo2Badge, highlightTableRow } from "./features/search/searchUI.js";
const geoserverUrl = GEOSERVER_BASE_URL;
const stringDatatype = "xsd:string";
const intDatatype = "xsd:int";
const floatDatatype = "xsd:float";
const shortDatatype = "xsd:short";
const ebenenDropdownText = "treeclimatedb:treesimport";
let tableFristHeaderString = "Koordinaten";

/**
 * Lädt und sortiert Baumeigenschaften vom Server.
 * @returns {Promise<Array>} Sortierte Liste der Baumeigenschaften
 */
export async function loadTreeSearchProperties() {
  let eigenschaften = await geoServerServices.fetchTreeSearchSchema();
  return sortPropertiesByName(eigenschaften);
}

/**
 * Lädt und sortiert Flächeneigenschaften vom Server.
 * @returns {Promise<Array>} Sortierte Liste der Flächeneigenschaften
 */
export async function loadAreaSearchProperties() {
  let eigenschaften = await geoServerServices.fetchAreaSearchSchema();
  return sortPropertiesByName(eigenschaften);
}

function sortPropertiesByName(eigenschaften) {
  if (!eigenschaften) return [];
  return [...eigenschaften].sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });
}

function lowercaseFirst(text) {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

export async function searchQuery(searchAll, preBuildSearchURL) {
  zuruecksetzenPopupAnzeige();
  let actualMap = getActualMap();
  let map = actualMap.globalMap;
  let CO2SaveSum = 0;

  document.getElementById("data-table-content").innerHTML = "";
  removeMapLayer(map, "Suchergebnis");

  const eigenschaftenDropdown = document.getElementById("baumEigenschaft");

  if (!searchAll && (!preBuildSearchURL || preBuildSearchURL.length == 0)) {
    if (!checkInputFields() || eigenschaftenDropdown.selectedIndex == 0) {
      warningMessage(
        "Bitte Suchfled richtig ausfüllen",
        "Es ist keine Eigenschaft gewählt oder kein Suchbegriff eingegeben"
      );
      return;
    }
  }

  showLoading(true);

  let generatedUrl;
  if (searchAll) {
    generatedUrl = `${GEOSERVER_BASE_URL}ows?service=WFS&version=2.0.0&request=GetFeature&typeName=${GEOSERVER_WORKSPACE}:treesinitial&propertyName=lokalenr,vitalitaet,geokooridnaten,laengengrad,breitengrad&outputFormat=application/json`;
  } else if (preBuildSearchURL) {
    generatedUrl = preBuildSearchURL;
  } else {
    let eigenschaftenDropdownText = eigenschaftenDropdown.options[eigenschaftenDropdown.selectedIndex].text;
    eigenschaftenDropdownText = lowercaseFirst(eigenschaftenDropdownText);
    const eigenschaftenDropdownDatentyp = "xsd:string";
    const suchBegriff = document.getElementById("searchInputField").value;
    generatedUrl = buildSearchUrl(geoserverUrl, ebenenDropdownText, eigenschaftenDropdownText, eigenschaftenDropdownDatentyp, suchBegriff);
  }

  const response = await fetch(generatedUrl);
  const data = await response.json();

  if (data.features.length == 0) {
    warningMessage("Kein Ergebnis", "Zu Ihrer Anfrage wurde kein Ergebnis gefunden.");
    showLoading(false);
    return;
  }

  const col = [tableFristHeaderString];
  for (const feature of data.features) {
    for (const key in feature.properties) {
      if (col.indexOf(key) === -1) col.push(key);
    }
  }

  const sourceSingle = new VectorSource();

  for (const feature of data.features) {
    if (!feature || !feature.bbox || !feature.bbox[1] || !feature.bbox[0] || !feature.geometry || !feature.id) continue;

    const long = feature.properties.laengengrad;
    const lat = feature.properties.breitengrad;

    if (!searchAll) {
      getDryDensity(feature.properties.baumart);
      CO2SaveSum += calculateCo2StorageByDimensions(
        feature.properties.hoehe,
        circumferenceToDiameter(cmToMeter(feature.properties.umfang)),
        feature.properties.baumart
      );
    }

    const olFeature = new Feature({
      id: feature.id,
      geometry: new Point(fromLonLat([long, lat], "EPSG:3857", "EPSG:4326")),
    });

    for (const [key, value] of Object.entries(feature.properties)) {
      olFeature.set(key, value);
    }

    olFeature.setStyle(new Style({
      image: new Icon({
        scale: 0.1,
        anchor: [0.5, 550],
        anchorXUnits: "fraction",
        anchorYUnits: "pixels",
        src: getTreeVitalityIcon(feature.properties.vitalitaet),
      }),
    }));

    sourceSingle.addFeature(olFeature);
  }

  renderSearchResultTable(data.features, col);
  renderCo2Badge(CO2SaveSum);

  const geojson = new VectorLayer({
    title: "Suchergebnis",
    name: "Suchergebnis",
    source: sourceSingle,
  });

  geojson.getSource().on("addfeature", function () {
    map.getView().fit(geojson.getSource().getExtent(), {
      duration: 1590,
      size: map.getSize(),
    });
  });

  hideLayer(map, "Bäume - Gruppiert");
  hideLayer(map, "Bäume - Alle");
  map.addLayer(geojson);
  showLoading(false);

  map.on("click", highlight);
  return data.features;
}

function buildSearchUrl(
  geoserverUrl,
  layerName,
  property,
  datatype,
  searchTerm
) {
  let searchMode = "LIKE";
  let forgedSearchInput = "";

  if (datatype == "xsd:string") {
    searchMode = "LIKE";
    forgedSearchInput = "%" + searchTerm + "%";
  }
  if (
    datatype == shortDatatype ||
    datatype == intDatatype ||
    datatype == floatDatatype
  ) {
    searchMode = "=";
    forgedSearchInput = searchTerm;
  }

  var url =
    geoserverUrl +
    "ows?service=WFS&version=2.0.0&request=GetFeature&typeName=" +
    layerName +
    "&CQL_FILTER=" +
    property +
    "+" +
    searchMode +
    "+'" +
    forgedSearchInput +
    "'&outputFormat=application/json";
  return encodeURI(url);
}

function highlight(evt) {
  const feature = getGlobalMap().forEachFeatureAtPixel(evt.pixel, f => f);
  if (feature != null) {
    highlightTableRow(feature.values_.lokalenr);
  }
}

/**
 * Verarbeitet die Live-Suche für Bäume.
 * @param {string} property - Die gewählte Baumeigenschaft
 * @param {string} searchTerm - Der Suchbegriff
 * @returns {Promise<{displayed: Array, all: Array, hasMore: boolean}|null>}
 */
export async function fetchLiveTreeSearch(property, searchTerm) {
  let trees = await geoServerServices.fetchTreesByProperty(
    lowercaseFirst(property),
    searchTerm
  );

  if (!trees) return null;

  let results = [];
  trees.forEach((feature) => {
    Object.entries(feature.properties).forEach(([key, value]) => {
      if (key == lowercaseFirst(property)) {
        trees.indexOf(value) === -1 ? results.push(value) : null;
      }
    });
  });

  let counted = countOccurrences(results);
  let sorted = sortDescendingByCount(counted);

  const hasMore = sorted.length > 10;
  const displayed = hasMore ? sorted.slice(0, 10) : sorted;

  return { displayed, all: sorted, hasMore };
}

/**
 * Verarbeitet die Live-Suche für Flächen.
 * @param {string} property - Die gewählte Flächeneigenschaft
 * @param {string} searchTerm - Der Suchbegriff
 * @returns {Promise<{areas: Array, countPerArea: Object}|null>}
 */
export async function fetchLiveAreaSearch(property, searchTerm) {
  let areas = await geoServerServices.fetchAreasByProperty(
    lowercaseFirst(property),
    searchTerm
  );

  if (!areas) return null;

  let areaList = [];
  areas.forEach((feature) => {
    Object.entries(feature.properties).forEach(([key, value]) => {
      if (key == lowercaseFirst(property)) {
        areas.indexOf(value) === -1 ? areaList.push(value) : null;
      }
    });
  });

  const countPerArea = countOccurrences(areaList);
  areaList = [...new Set(areaList)].sort();

  return { areas: areaList, countPerArea };
}

/**
 * @param {string} property - Flächeneigenschaft
 * @param {string} searchTerm - Suchwert
 * @returns {Promise<import('./Services/GeoServerServices').AreaFeature[]>}
 */
export async function loadAreasForSearch(property, searchTerm) {
  return await geoServerServices.fetchAreasByProperty(
    lowercaseFirst(property),
    searchTerm
  );
}

/** @returns {Promise<import('./Services/GeoServerServices').AreaFeature[]>} */
export async function loadAllAreas() {
  return await geoServerServices.fetchAllAreas();
}

/** @returns {Promise<import('./Services/GeoServerServices').TreeFeature[]>} */
export async function loadAllDeletedTrees() {
  return await geoServerServices.fetchAllDeletedTrees();
}

function countOccurrences(arr) {
  const counts = {};
  arr.forEach((value) => {
    counts[value] = counts[value] ? counts[value] + 1 : 1;
  });
  return counts;
}

function sortDescendingByCount(counted) {
  return Object.entries(counted).sort((a, b) => b[1] - a[1]);
}
